import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import ExpectedLogo from '@/assets/logos/expected-full.svg?react';
import { cn } from '@/lib/utils';
import { PATIENT_HOME_PATH } from '@/lib/patientPortalApi';

export const MARKETING_SITE_URL = 'https://expectedhealthcare.com';

interface PatientPortalLayoutProps {
  children: ReactNode;
  /** Present once a patient is signed in: renders the page links and Sign out. */
  onSignOut?: () => void;
  signOutError?: string | null;
}

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  cn(
    'rounded-md px-2 py-1 font-patient-display text-sm font-semibold hover:bg-patient-ground focus-visible:ring-[3px] focus-visible:ring-primary/40 focus-visible:outline-none',
    isActive ? 'text-primary' : 'text-patient-ink-muted'
  );

/**
 * The patient pages' frame on the patient kit: a small branded header that
 * links back to the marketing site, one content column, no staff chrome.
 * Nothing here carries UNMASK_PROPS: a patient's screen is recorded masked,
 * labels included (patientReplayMasking.test.tsx).
 */
export function PatientPortalLayout({
  children,
  onSignOut,
  signOutError,
}: PatientPortalLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-patient-ground font-patient-body text-patient-ink [color-scheme:light]">
      <header className="border-b border-patient-line bg-white">
        <div className="mx-auto flex w-full max-w-xl items-center justify-between gap-3 px-5 py-3">
          <a
            href={MARKETING_SITE_URL}
            aria-label="Expected Healthcare"
            className="flex items-center"
          >
            <ExpectedLogo className="h-7 w-auto" />
          </a>
          {onSignOut ? (
            <nav aria-label="Your account" className="flex items-center gap-1">
              <NavLink to={PATIENT_HOME_PATH} end className={navLinkClass}>
                Requests
              </NavLink>
              <NavLink
                to={`${PATIENT_HOME_PATH}/profile`}
                className={navLinkClass}
              >
                Profile
              </NavLink>
              <button
                type="button"
                onClick={onSignOut}
                className={navLinkClass({ isActive: false })}
              >
                Sign out
              </button>
            </nav>
          ) : null}
        </div>
      </header>
      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col px-5 pt-8 pb-12">
        {signOutError ? (
          <p role="alert" className="text-patient-error mb-6">
            {signOutError}
          </p>
        ) : null}
        {children}
      </main>
    </div>
  );
}
